import { Palette as PaletteType } from "@mui/material/styles";

declare module "@mui/material/styles" {
  // — Custom tokens on PaletteColor (following MUI docs: https://mui.com/material-ui/customization/palette/#typescript)
  interface PaletteColor {
    // Layer numeric scale (used by secondary)
    9: string;
    "9/75": string;
    "9/50": string;
    "9/25": string;
    8: string;
    "8/75": string;
    "8/50": string;
    "8/25": string;
    7: string;
    "7/75": string;
    "7/50": string;
    "7/25": string;
    6: string;
    "6/75": string;
    "6/50": string;
    "6/25": string;
    5: string;
    "5/75": string;
    "5/50": string;
    "5/25": string;
    4: string;
    "4/75": string;
    "4/50": string;
    "4/25": string;
    3: string;
    "3/75": string;
    "3/50": string;
    "3/25": string;
    2: string;
    "2/75": string;
    "2/50": string;
    "2/25": string;
    1: string;
    "1/75": string;
    "1/50": string;
    "1/25": string;
  }

  interface SimplePaletteColorOptions {
    // Layer numeric scale
    9?: string;
    "9/75"?: string;
    "9/50"?: string;
    "9/25"?: string;
    8?: string;
    "8/75"?: string;
    "8/50"?: string;
    "8/25"?: string;
    7?: string;
    "7/75"?: string;
    "7/50"?: string;
    "7/25"?: string;
    6?: string;
    "6/75"?: string;
    "6/50"?: string;
    "6/25"?: string;
    5?: string;
    "5/75"?: string;
    "5/50"?: string;
    "5/25"?: string;
    4?: string;
    "4/75"?: string;
    "4/50"?: string;
    "4/25"?: string;
    3?: string;
    "3/75"?: string;
    "3/50"?: string;
    "3/25"?: string;
    2?: string;
    "2/75"?: string;
    "2/50"?: string;
    "2/25"?: string;
    1?: string;
    "1/75"?: string;
    "1/50"?: string;
    "1/25"?: string;
  }

  // — Node palette (resolved vs options, following MUI's own Palette/PaletteOptions pattern)
  interface NodePaletteColors {
    generator: PaletteColor;
    prompt: PaletteColor;
    style: PaletteColor;
    insert: PaletteColor;
    reference: PaletteColor;
    utility: PaletteColor;
    refine: PaletteColor;
    disabled: string;
  }

  interface NodePaletteColorOptions {
    generator?: SimplePaletteColorOptions;
    prompt?: SimplePaletteColorOptions;
    style?: SimplePaletteColorOptions;
    insert?: SimplePaletteColorOptions;
    reference?: SimplePaletteColorOptions;
    utility?: SimplePaletteColorOptions;
    refine?: SimplePaletteColorOptions;
    disabled?: string;
  }

  // — Custom entries on Palette
  interface Palette {
    model: PaletteType["success"];
    node: NodePaletteColors;
  }
  interface PaletteOptions {
    model?: PaletteOptions["success"];
    node?: NodePaletteColorOptions;
  }
}

export const Palette = {
  TRANSPARENT: "rgba(255,255,255,0)",
  WHITE: "#ffffff",
  BLACK: "#000000",
  PRIMARY: {
    "dark/50": "#3B0664",
    dark: "#600FC2",
    main: "#600FC2",
    light: "#A48CFD",
    "light/50": "rgba(164,140,253,0.5)",
    indigo: "#4A53DD",
  },
  SECONDARY: {
    9: "#0F1014",
    "9/75": "rgba(15,16,20,0.75)",
    "9/50": "rgba(15,16,20,0.5)",
    "9/25": "rgba(15,16,20,0.25)",
    8: "#15161C",
    "8/75": "rgba(21,22,28,0.75)",
    "8/50": "rgba(21,22,28,0.5)",
    "8/25": "rgba(21,22,28,0.25)",
    7: "#191A22",
    "7/75": "rgba(25,26,34,0.75)",
    "7/50": "rgba(25,26,34,0.5)",
    "7/25": "rgba(25,26,34,0.25)",
    6: "#20212D",
    "6/75": "rgba(32,33,45,0.75)",
    "6/50": "rgba(32,33,45,0.5)",
    "6/25": "rgba(32,33,45,0.25)",
    5: "#2F3141",
    "5/75": "rgba(47,49,65,0.75)",
    "5/50": "rgba(47,49,65,0.5)",
    "5/25": "rgba(47,49,65,0.25)",
    4: "#404159",
    "4/75": "rgba(64,65,89,0.75)",
    "4/50": "rgba(64,65,89,0.5)",
    "4/25": "rgba(64,65,89,0.25)",
    3: "#858699",
    "3/75": "rgba(133,134,153,0.75)",
    "3/50": "rgba(133,134,153,0.5)",
    "3/25": "rgba(133,134,153,0.25)",
    2: "#D2D3E0",
    "2/75": "rgba(210,211,224,0.75)",
    "2/50": "rgba(210,211,224,0.5)",
    "2/25": "rgba(210,211,224,0.25)",
    1: "#FAFAFF",
    "1/75": "rgba(250,250,255,0.75)",
    "1/50": "rgba(250,250,255,0.5)",
    "1/25": "rgba(250,250,255,0.25)",
  },
  SUCCESS: {
    "dark/50": "rgba(94,233,181,0.2)",
    dark: "#004432",
    main: "#5EE9B5",
    light: "#72FDC9",
  },
  WARNING: {
    "dark/50": "rgba(255,220,91,0.3)",
    dark: "#4E3F0A",
    main: "#FFDC5B",
    light: "#FFE68A",
  },
  ERROR: {
    "dark/50": "rgba(255,108,111,0.3)",
    dark: "#551820",
    main: "#FF6C6F",
    light: "#FD8486",
  },
  MODEL: {
    "dark/50": "rgba(83,234,253,0.25)",
    dark: "#094959",
    main: "#53EAFD",
    light: "#8DF2FF",
  },
  NODE_GENERATOR: {
    // values will match main color after theme update
    dark: "#21024B",
    main: "#600FC2",
    light: "#AE9DEC",
    contrastText: "#F5F2FF",
  },
  NODE_PROMPT: {
    // values will match info color after theme update
    dark: "#0E1A39",
    main: "#5476D9",
    light: "#B5DDFC",
    contrastText: "#EEF0FB",
  },
  NODE_STYLE: {
    // values will match success color after theme update
    dark: "#002614",
    main: "#017F4E",
    light: "#A3F9C0",
    contrastText: "#EBFFF3",
  },
  NODE_INSERT: {
    // values will match error color after theme update
    dark: "#2D0001",
    main: "#FF5559",
    light: "#F59FA0",
    contrastText: "#FFECED",
  },
  NODE_REFERENCE: {
    // values will match attention color after theme update
    dark: "#3D3200",
    main: "#FFDC5B",
    light: "#F1E288",
    contrastText: "#191300",
  },
  NODE_UTILITY: {
    // values will match secondary color after theme update
    dark: "#191A22",
    main: "#404159",
    light: "#DCDCDB",
    contrastText: "#F3F3F6",
  },
  NODE_REFINE: {
    // values will match warning color after theme update
    dark: "#462000",
    main: "#FF9347",
    light: "#F5D39F",
    contrastText: "#200B00",
  },
  NODE_DISABLED: "rgba(255,255,255,0.2)",
};
